import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type NotificationTemplateInput, notificationTemplateService } from '@/services/notification-template.service'

/** TanStack query keys of the template library. */
export const notificationTemplateKeys = {
	all: ['notification-templates'] as const,
	detail: (id: string) => ['notification-templates', 'detail', id] as const,
}

/** A create (`id` null) or an update of template `id`. */
export interface NotificationTemplateSave {
	id: string | null
	input: NotificationTemplateInput
}

/**
 * The tenant's whole template library (TO-140 REQ-007, library order) with its
 * create/update and delete; a stored change refetches the library.
 */
export function useNotificationTemplates() {
	const client = useQueryClient()
	const query = useQuery({
		queryKey: notificationTemplateKeys.all,
		queryFn: () => notificationTemplateService.list(),
	})
	const invalidate = () => client.invalidateQueries({ queryKey: notificationTemplateKeys.all })
	const save = useMutation({
		mutationFn: ({ id, input }: NotificationTemplateSave) =>
			id === null ? notificationTemplateService.create(input) : notificationTemplateService.update(id, input),
		onSuccess: invalidate,
	})
	const remove = useMutation({
		mutationFn: (id: string) => notificationTemplateService.remove(id),
		onSuccess: invalidate,
	})
	return {
		templates: query.data ?? [],
		loading: query.isPending,
		loadFailed: query.isError,
		reload: () => void query.refetch(),
		save,
		remove,
	}
}
